import { useEffect, useState } from 'react';
import type { Session } from '../App';
import { Container, PageHeader } from '../App';

interface Props { session: Session; }

interface GuildView {
  id: string;
  name: string;
  member_count: number;
  max_members: number;
  join_policy: string;
}

interface MailMessage {
  id: string;
  from: string;
  to: string;
  subject: string;
  body: string;
  category: string;
  read: boolean;
  claimed: boolean;
}

export default function Social({ session }: Props) {
  const playerId = session.playerId;
  const [guilds, setGuilds] = useState<GuildView[]>([]);
  const [inbox, setInbox] = useState<MailMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Guild create form
  const [guildId, setGuildId] = useState('');
  const [guildName, setGuildName] = useState('');

  // Mail form
  const [mailTo, setMailTo] = useState('');
  const [mailSubject, setMailSubject] = useState('');
  const [mailBody, setMailBody] = useState('');

  const base = `/api/v1/games/${session.gameId}/modules/social`;

  const reloadGuilds = () => {
    fetch(`${base}/guilds`)
      .then(r => r.json())
      .then((d: { guilds: GuildView[] }) => setGuilds(d.guilds))
      .catch(e => setError(e.message));
  };

  const reloadInbox = () => {
    fetch(`${base}/inbox/${playerId}`)
      .then(r => r.json())
      .then((d: { messages: MailMessage[] }) => setInbox(d.messages))
      .catch(e => setError(e.message));
  };

  useEffect(() => { reloadGuilds(); reloadInbox(); }, [session.gameId, playerId]);

  const createGuild = async () => {
    if (!guildId.trim() || !guildName.trim()) return;
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${base}/guilds`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guild_id: guildId.trim(), guild_name: guildName.trim(), creator_id: playerId }),
      });
      if (!res.ok) throw new Error(await res.text());
      setGuildId(''); setGuildName('');
      reloadGuilds();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const joinGuild = async (gid: string) => {
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${base}/guilds/${gid}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ player_id: playerId }),
      });
      if (!res.ok) throw new Error(await res.text());
      reloadGuilds();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const leaveGuild = async (gid: string) => {
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${base}/guilds/${gid}/leave`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ player_id: playerId }),
      });
      if (!res.ok) throw new Error(await res.text());
      reloadGuilds();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const sendMail = async () => {
    if (!mailTo.trim() || !mailSubject.trim()) return;
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${base}/mail/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from: playerId, to: mailTo.trim(), subject: mailSubject.trim(), body: mailBody.trim(), category: 'player' }),
      });
      if (!res.ok) throw new Error(await res.text());
      setMailTo(''); setMailSubject(''); setMailBody('');
      reloadInbox();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <Container>
      <PageHeader
        title="Social"
        subtitle="Guilds, friends, and mail."
      />
      {error && <div className="error-banner">{error}</div>}

      {/* Create Guild */}
      <div className="card mb-5 p-4">
        <h3 className="text-sm font-semibold mb-3">Create Guild</h3>
        <div className="flex gap-3 items-end">
          <div>
            <label className="text-xs text-fg-muted block mb-1">Guild ID</label>
            <input className="input" placeholder="heroes" value={guildId} onChange={e => setGuildId(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-fg-muted block mb-1">Guild Name</label>
            <input className="input" placeholder="Heroes Guild" value={guildName} onChange={e => setGuildName(e.target.value)} />
          </div>
          <button className="btn btn-primary" disabled={busy || !guildId.trim() || !guildName.trim()} onClick={createGuild}>Create</button>
        </div>
      </div>

      {/* Guild List */}
      {guilds.length > 0 && (
        <div className="card mb-5">
          <h3 className="text-sm font-semibold p-4 pb-2">Guilds</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Members</th>
                  <th>Policy</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {guilds.map(g => (
                  <tr key={g.id}>
                    <td className="font-mono text-xs">{g.id}</td>
                    <td>{g.name}</td>
                    <td>{g.member_count}/{g.max_members}</td>
                    <td><span className="badge badge-muted">{g.join_policy}</span></td>
                    <td className="flex gap-2">
                      <button className="btn btn-sm" disabled={busy} onClick={() => joinGuild(g.id)}>Join</button>
                      <button className="btn btn-sm" disabled={busy} onClick={() => leaveGuild(g.id)}>Leave</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {guilds.length === 0 && (
        <div className="empty mb-5">
          <div className="text-fg-2 font-medium mb-1">No guilds</div>
          <div className="text-xs text-fg-muted">Create a guild above to get started.</div>
        </div>
      )}

      {/* Send Mail */}
      <div className="card mb-5 p-4">
        <h3 className="text-sm font-semibold mb-3">Send Mail</h3>
        <div className="flex gap-3 items-end flex-wrap">
          <div>
            <label className="text-xs text-fg-muted block mb-1">To (player ID)</label>
            <input className="input" placeholder="recipient id" value={mailTo} onChange={e => setMailTo(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-fg-muted block mb-1">Subject</label>
            <input className="input" placeholder="Hello!" value={mailSubject} onChange={e => setMailSubject(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-fg-muted block mb-1">Body</label>
            <input className="input" placeholder="Message body" value={mailBody} onChange={e => setMailBody(e.target.value)} />
          </div>
          <button className="btn btn-primary" disabled={busy || !mailTo.trim() || !mailSubject.trim()} onClick={sendMail}>Send</button>
        </div>
      </div>

      {/* Inbox */}
      {inbox.length > 0 && (
        <div className="card">
          <h3 className="text-sm font-semibold p-4 pb-2">Inbox</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>From</th>
                  <th>Subject</th>
                  <th>Category</th>
                  <th>Read</th>
                </tr>
              </thead>
              <tbody>
                {inbox.map(m => (
                  <tr key={m.id}>
                    <td className="font-mono text-xs">{m.from}</td>
                    <td>{m.subject}</td>
                    <td><span className="badge badge-muted">{m.category}</span></td>
                    <td>{m.read ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {inbox.length === 0 && (
        <div className="empty">
          <div className="text-fg-2 font-medium mb-1">Inbox empty</div>
          <div className="text-xs text-fg-muted">Send a mail to this player to see it here.</div>
        </div>
      )}
    </Container>
  );
}
